;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { Sale, SaleDeclineRequest, User } from '@qogita/canary-client'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { browserLogger } from 'src/core/logger/browser'
import { env } from 'src/env.mjs'

const SEGMENT_WRITE_KEY = env.NEXT_PUBLIC_SEGMENT_WRITE_KEY

const analytics = AnalyticsBrowser.load(
  {
    writeKey: SEGMENT_WRITE_KEY,
    cdnURL: 'https://analytics.qogita.com',
  },
  {
    integrations: {
      'Segment.io': {
        apiHost: 'analyticsapi.qogita.com/v1',
      },
    },
  },
)

type AnalyticsEvent =
  | 'User Signed In'
  | 'User Signed Out'
  | 'User Phone Verified'
  | 'User Email Verified'
  | 'User Tried To Sign In With Buyer Account'
  | 'Sale Viewed'
  | 'Sale Viewed (New)'
  | 'Sale Saleline Quantities Confirmed'
  | 'Sale Shipping Fee Updated'
  | 'Sale VAT Updated'
  | 'Sale Lines Downloaded'
  | 'Sale Proforma Downloaded'
  | 'Sale Invoice Downloaded'
  | 'Salelines Updated via Invoice'
  | 'Sale Payment Confirmation Downloaded'
  | 'Sale Commercial Invoice Downloaded'
  | 'Sale Shipping Details Track And Trace Number Added'
  | 'Sale Shipping Details Track And Trace Number Removed'
  | 'Sale Additional Documents Uploaded'
  | 'Sale Addionional Documents Type Updated'
  | 'Sale Payment Triggered'
  | 'Sale Declined'
  | 'Sale View All Claims Clicked'
  | 'SaleLine Updated'
  | 'Sale Ship With Qogita Confirmed'
  | 'Dashboard View All Current Orders Clicked'
  | 'Dashboard View All Price Lists Clicked'
  | 'Dashboard New Order Clicked'
  | 'Dashboard Expiring Order Clicked'
  | 'Dashboard Learn More Inventory Last Processed Clicked'
  | 'Dashboard View All Trending Products Clicked'
  | 'Dashboard View All Insights Clicked'
  | 'HelpCenter Seller Handbook Clicked'
  | 'HelpCenter Terms And Conditions Clicked'
  | 'HelpCenter Resale Certificates Clicked'
  | 'HelpCenter Report An Issue Clicked'
  | 'Inventory Download Overview Clicked'
  | 'Inventory Copy Link Clicked'
  | 'Inventory Boost Revenue Contact Us Clicked'
  | 'Profile Discount Updated'
  | 'Profile Chamber Of Commerce Number Updated'
  | 'Profile Billing Address Updated'
  | 'Profile Shipping Address Updated'
  | 'Profile Payment Details Updated'
  | 'MarketingPage Portal Next Step Clicked'
  | 'MarketingPage FAQ Expanded'
  | 'LoginPage Typeform Opened'
  | 'Footer Link Clicked'
  | 'PausedSellerModal Contact Us Clicked'
  | 'PausedSellerInlineNotification Contact Us Clicked'
  | 'Claims Order ID Clicked'
  | 'TrendingProducts Trending Product Modal Opened'
  | 'TrendingProducts Download Overview Clicked'
  | 'Inventory Status Filter Applied'
  | 'Inventory Filter Applied'
  | 'Orders Filter Applied'
  | 'Orders Downloaded'
  | 'Poor Packaging Copy Displayed'

/**
 * Performs a Segment `track` call to record a user action. This call must
 * follow convention. See https://github.com/qogita/web#conventions.
 * @param object The name of the Object in Title Case.
 * @param action The name of the Action in Title Case.
 * @param properties An optional object containing extra information about the
 *                   Action. Keys should be written in camelCase.
 */
function track(event: AnalyticsEvent, properties: object | undefined) {
  if (!env.NEXT_PUBLIC_SEGMENT_WRITE_KEY) return
  try {
    analytics.track(event, properties)
  } catch (error) {
    browserLogger.error(error)
  }
}

type Identify = (userId: string, traits?: { [key: string]: unknown }) => void

const identify: Identify = (userId, traits) => {
  if (!env.NEXT_PUBLIC_SEGMENT_WRITE_KEY) return
  try {
    analytics.identify(userId, traits)
  } catch (error) {
    browserLogger.error(error)
  }
}

export const identifyUser = (user: User) => {
  identify(user.qid, {
    email: user.email,
    name: user.account,
    createdAt: user.createdAt,
    company: user.company,
    isKeyAccount: !!user.accountManager,
    sellerStatus: user.sellerStatus,
    pauseReason: user.pauseReason,
    expectedPauseEnd: user.expectedPauseEnd,
  })
}

export const removeUserIdentificationFromThirdParties = () => {
  try {
    analytics.reset()
  } catch (error) {
    browserLogger.error(error)
  }
}

export const identifyPage = (name: string, query: object) => {
  try {
    analytics.page(undefined, name, query)
  } catch (error) {
    browserLogger.error(error)
  }
}

export const trackUserSignedOut = () => {
  track('User Signed Out', undefined)
}

export const trackSaleViewedNew = (sale: Sale) => {
  track('Sale Viewed (New)', {
    saleQid: sale.qid,
    status: sale.status,
    order: sale.order,
  })
}

export const trackSaleSalelineQuantitiesConfirmed = (sale: Sale) => {
  track('Sale Saleline Quantities Confirmed', {
    saleQid: sale.qid,
  })
}

export const trackSaleLinesDownloaded = (sale: Sale) => {
  track('Sale Lines Downloaded', {
    saleQid: sale.qid,
    order: sale.order,
  })
}

export const trackSalePaymentConfirmationDownloaded = (sale: Sale) => {
  track('Sale Payment Confirmation Downloaded', {
    saleQid: sale.qid,
    order: sale.order,
  })
}

export const trackSaleInvoiceDownloaded = (sale: Sale) => {
  track('Sale Invoice Downloaded', {
    saleQid: sale.qid,
    order: sale.order,
  })
}

export const trackSaleCommercialInvoiceDownloaded = ({
  sale,
  ...props
}: {
  sale: Sale
  isPreGenerated: boolean
}) => {
  track('Sale Commercial Invoice Downloaded', {
    ...props,
    saleQid: sale.qid,
    order: sale.order,
  })
}

export const trackSaleAdditionalDocumentsUploaded = (
  sale: Pick<Sale, 'qid' | 'order'>,
) => {
  track('Sale Additional Documents Uploaded', {
    saleQid: sale.qid,
    order: sale.order,
  })
}

export const trackSaleAdditionalDocumentsTypeUpdated = ({
  saleQid,
  order,
  documentQid,
  documentType,
}: {
  saleQid: Sale['qid']
  order: Sale['order']
  documentQid: string
  documentType: string
}) => {
  /*
    Please don't fix the typo because we lose the tracking history.
   */
  track('Sale Addionional Documents Type Updated', {
    saleQid,
    order,
    documentQid,
    documentType,
  })
}

export const trackSalePaymentTriggered = (saleQid: string) => {
  track('Sale Payment Triggered', {
    saleQid,
  })
}

export const trackSaleDeclined = (
  saleQid: string,
  data: SaleDeclineRequest,
) => {
  track('Sale Declined', {
    saleQid,
    ...data,
  })
}

export const trackSaleLineUpdated = (data: {
  saleQid: string
  saleLineQid: string
  price: string
  quantity: string
  quantityReductionReason: string
}) => {
  track('SaleLine Updated', data)
}

export const trackSalelinesUpdatedViaInvoiceUploaded = (data: {
  saleQid: string
  gtinColumnName: string
  quantityColumnName: string
  salelineCount: number
}) => {
  track('Salelines Updated via Invoice', data)
}

export const trackDashboardViewAllCurrentOrdersClicked = () => {
  track('Dashboard View All Current Orders Clicked', undefined)
}

export const trackDashboardViewAllPriceListsClicked = () => {
  track('Dashboard View All Price Lists Clicked', undefined)
}

export const trackDashboardNewOrderClicked = (sale: Sale) => {
  track('Dashboard New Order Clicked', sale)
}

export const trackDashboardExpiringOrderClicked = (sale: Sale) => {
  track('Dashboard Expiring Order Clicked', sale)
}

export const trackUserPhoneVerified = () => {
  track('User Phone Verified', undefined)
}

export const trackUserEmailVerified = () => {
  track('User Email Verified', undefined)
}

export const trackFooterLinkClicked = (props: { link: string }) => {
  track('Footer Link Clicked', props)
}

export const trackInventoryFiltered = (props: {
  type: 'status' | 'brand' | 'sort'
  brand?: string
  status?: string
  sort?: string
  source: 'statisticsCards' | 'brandsFilter' | 'inventoryTable'
}) => {
  track('Inventory Filter Applied', props)
}

export const trackOrdersFilterApplied = (props: {
  type: 'next-action' | 'destination' | 'sort'
  value: string
}) => {
  track('Orders Filter Applied', props)
}

export const trackDownload = (props: {
  type: 'bulk-order-lines' | 'bulk-invoices'
}) => {
  track('Orders Downloaded', props)
}

export const trackSaleShipWithQogitaConfirmed = (props: {
  saleQid: string
}) => {
  track('Sale Ship With Qogita Confirmed', props)
}

export const trackPoorPackagingCopyDisplayed = () => {
  track('Poor Packaging Copy Displayed', undefined)
}
